import { createRouter, createWebHistory } from 'vue-router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/store/user';
const NestedLayout = () => import('@/layouts/NestedLayout.vue');

/** Check mobile at call time — ref-based composable isn't ready during router init */
function checkIsMobile(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < 768;
}

const getLayoutComponent = () => {
  return checkIsMobile()
    ? import('@/layouts/MobileLayout.vue')
    : import('@/layouts/DesktopLayout.vue');
};

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/login/LoginView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/register',
      name: 'Register',
      component: () => import('@/views/register/RegisterView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/m/login',
      name: 'MobileLogin',
      component: () => import('@/views/mobile/MobileLogin.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/m/register',
      name: 'MobileRegister',
      component: () => import('@/views/mobile/MobileRegister.vue'),
      meta: { requiresAuth: false },
    },
    {
      // S-23：首次登录强制改密（独立于主布局，桌面/移动共用）
      path: '/change-password',
      name: 'ForceChangePassword',
      component: () => import('@/views/system/ForceChangePasswordView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/m',
      component: () => import('@/layouts/MobileLayout.vue'),
      redirect: '/m/dashboard',
      meta: { requiresAuth: true },
      children: [
        {
          path: 'dashboard',
          name: 'MobileDashboard',
          component: () => import('@/views/mobile/MobileDashboard.vue'),
          meta: { title: '仪表盘' },
        },
        {
          path: 'tester',
          name: 'MobileTester',
          component: () => import('@/views/mobile/MobileTester.vue'),
          meta: { title: '接口测试' },
        },
        {
          path: 'monitor',
          name: 'MobileMonitor',
          component: () => import('@/views/mobile/MobileMonitor.vue'),
          meta: { title: '监控' },
        },
        {
          path: 'manage',
          name: 'MobileManage',
          component: () => import('@/views/mobile/MobileManage.vue'),
          meta: { title: '管理' },
        },
        {
          path: 'profile',
          name: 'MobileProfile',
          component: () => import('@/views/mobile/MobileProfile.vue'),
          meta: { title: '个人中心' },
        },
        {
          path: 'about',
          name: 'MobileAbout',
          component: () => import('@/views/mobile/MobileAbout.vue'),
          meta: { title: '关于' },
        },
      ],
    },
    {
      path: '/',
      component: getLayoutComponent,
      redirect: '/dashboard',
      meta: { requiresAuth: true },
      children: [
        {
          path: 'dashboard',
          name: 'Dashboard',
          component: () => import('@/views/dashboard/DashboardView.vue'),
          meta: { title: '仪表盘', icon: 'Odometer', permission: 'system:dashboard:view' },
        },
        {
          path: 'gateway',
          name: 'Gateway',
          component: NestedLayout,
          redirect: '/gateway/providers',
          meta: { title: 'API 网关', icon: 'Connection' },
          children: [
            {
              path: 'providers',
              name: 'Providers',
              component: () => import('@/views/gateway/providers/ProvidersView.vue'),
              meta: { title: '供应商管理', icon: 'Grid', permission: 'gateway:providers:view' },
            },
            {
              path: 'models',
              name: 'Models',
              component: () => import('@/views/gateway/models/ModelsView.vue'),
              meta: { title: '模型管理', icon: 'Cpu', permission: 'gateway:models:view' },
            },
            {
              path: 'routes',
              name: 'Routes',
              component: () => import('@/views/gateway/routes/RoutesView.vue'),
              meta: { title: '路由规则', icon: 'Guide', permission: 'gateway:routes:view' },
            },
            {
              path: 'tester',
              name: 'Tester',
              component: () => import('@/views/gateway/tester/TesterView.vue'),
              meta: { title: '接口测试', icon: 'VideoPlay', permission: 'gateway:tester:view' },
            },
            {
              path: 'api',
              name: 'ApiManage',
              component: () => import('@/views/system/api/ApiManageView.vue'),
              meta: { title: 'API 服务', icon: 'Link', permission: 'system:api-service:view' },
            },
          ],
        },
        {
          path: 'monitor',
          name: 'Monitor',
          component: NestedLayout,
          redirect: '/monitor/usage',
          meta: { title: '监控', icon: 'DataLine' },
          children: [
            {
              path: 'usage',
              name: 'Usage',
              component: () => import('@/views/monitor/usage/UsageView.vue'),
              meta: { title: '调用量统计', icon: 'TrendCharts', permission: 'monitor:usage:view' },
            },
            {
              path: 'latency',
              name: 'Latency',
              component: () => import('@/views/monitor/latency/LatencyView.vue'),
              meta: { title: '延迟监控', icon: 'Timer', permission: 'monitor:latency:view' },
            },
            {
              path: 'errors',
              name: 'Errors',
              component: () => import('@/views/monitor/errors/ErrorsView.vue'),
              meta: { title: '错误日志', icon: 'WarningFilled', permission: 'monitor:errors:view' },
            },
            {
              path: 'tokens',
              name: 'Tokens',
              component: () => import('@/views/monitor/tokens/TokensView.vue'),
              meta: { title: 'Token 消耗', icon: 'Coin', permission: 'monitor:tokens:view' },
            },
          ],
        },
        {
          path: 'system',
          name: 'System',
          component: NestedLayout,
          redirect: '/system/users',
          meta: { title: '系统管理', icon: 'Setting' },
          children: [
            {
              path: 'users',
              name: 'Users',
              component: () => import('@/views/system/users/UsersView.vue'),
              meta: { title: '用户管理', icon: 'User', permission: 'system:users:view' },
            },
            {
              path: 'roles',
              name: 'Roles',
              component: () => import('@/views/system/roles/RolesView.vue'),
              meta: { title: '角色与权限', icon: 'UserFilled', permission: 'system:roles:view' },
            },
            {
              path: 'tenants',
              name: 'Tenants',
              component: () => import('@/views/system/tenants/TenantsView.vue'),
              meta: { title: '租户管理', icon: 'OfficeBuilding', permission: 'system:tenants:view' },
            },
            {
              path: 'auth-config',
              name: 'AuthConfig',
              component: () => import('@/views/system/auth/AuthConfigView.vue'),
              meta: { title: '认证配置', icon: 'Lock', permission: 'system:auth:view' },
            },
            {
              path: 'about',
              name: 'About',
              component: () => import('@/views/system/AboutView.vue'),
              meta: { title: '关于', icon: 'InfoFilled', permission: 'system:about:view' },
            },
          ],
        },
        {
          path: 'profile',
          name: 'PersonalCenter',
          component: () => import('@/views/system/PersonalCenterView.vue'),
          meta: { title: '个人中心', icon: 'UserFilled', hidden: true },
        },
        {
          path: 'settings',
          name: 'PageSettings',
          component: () => import('@/views/system/PageSettingsView.vue'),
          meta: { title: '页面设置', icon: 'Setting', hidden: true },
        },
      ],
    },
  ],
});

router.beforeEach(async (to, _from, next) => {
  NProgress.start();
  const userStore = useUserStore();
  const isMobileBrowser = window.innerWidth < 768;

  // 移动端未登录 → /m/login
  if (to.meta.requiresAuth !== false && !userStore.isLoggedIn) {
    if (isMobileBrowser) {
      next({ path: '/m/login', query: { redirect: to.fullPath } });
    } else {
      next({ path: '/login', query: { redirect: to.fullPath } });
    }
  } else if (to.path === '/login' && userStore.isLoggedIn) {
    next(isMobileBrowser ? '/m/dashboard' : '/dashboard');
  } else if (to.path === '/m/login' && userStore.isLoggedIn) {
    next('/m/dashboard');
  } else if (userStore.isLoggedIn) {
    // 刷新后本地无 userInfo/权限码时先拉取一次，避免守卫误判
    await userStore.ensureProfileLoaded();

    // S-23：首次登录（或被管理员重置密码后）强制改密，改完前不允许进入其他页面
    if (userStore.userInfo?.mustChangePassword && to.path !== '/change-password') {
      next({ path: '/change-password', query: { redirect: to.fullPath } });
      return;
    }

    // D-5：路由级权限校验（meta.permission 与后端接口门槛一致），URL 直进无权页面时兜底
    const permission = to.meta.permission as string | undefined;
    if (permission && !userStore.can(permission)) {
      const fallback = isMobileBrowser ? '/m/dashboard' : '/dashboard';
      if (to.path === fallback) {
        next();
      } else {
        ElMessage.warning('暂无访问该页面的权限');
        next(fallback);
      }
      return;
    }
    next();
  } else {
    next();
  }
});

router.afterEach(() => {
  NProgress.done();
});

export default router;
